
import React, { useState, useEffect } from "react";
import api from "@helpers/api.js";
import Parser from "@helpers/parser.js";

import Table from "@components/Table";
import { Text, InfoBlock } from "@fragments";
import MetricCard from "@pages/Banking/components/MetricCard";
import StatusChip from "@fragments/StatusChip";

import "./styles.scss";

export default function FundsProjection() {
    const [loading, setLoading] = useState(true);
    const [projection, setProjection] = useState([]);
    const [summary, setSummary] = useState({ future: 0, paid: 0 });

    useEffect(() => {
        handleLoad();
    }, []);

    async function handleLoad() {
        setLoading(true);
        try {
            const { data } = await api.get("funds/projection?weeks=12");
            if (data) {
                if (data.raw) setProjection(data.raw);
                setSummary({
                    future: data.future || 0,
                    paid: data.paid || 0
                });
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }

    const rowsConfig = [
        {
            title: 'Valor',
            customRender: (data) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text.h5 className="bold">
                        R$ {Parser.price.brl(data.amount)}
                    </Text.h5>
                    <Text.span>BRL</Text.span>
                </div>
            )
        },
        {
            title: 'Data de disponibilização',
            render: (data) => {
                const [year, month, day] = data.date.split('-');
                const date = new Date(year, month - 1, day);
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Reset time for accurate day calculation

                const diffTime = date - today;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                const daysLabel = diffDays === 0 ? 'Hoje' : diffDays === 1 ? 'Amanhã' : `Em ${diffDays} dias`;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <StatusChip.Default text={daysLabel} />
                        <span>{date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                );
            }
        }
    ];

    if (projection.length === 0 && !loading && summary.future === 0 && summary.paid === 0) {
        return null;
    }

    return (
        <div className="funds-projection mt-5">
            <Text.h3 weight={500} className="mb-2">
                Projeção de disponibilização do saldo
            </Text.h3>

            <InfoBlock
                title="Como é calculada a projeção?"
                description="A projeção de disponibilização do saldo é calculada com base nas datas de recebíveis configuradas, a partir das cobranças pagas. É possível que cobranças sofram estorno ou chargeback, afetando a projeção abaixo."
                className="mb-2"
            />

            <div className="metrics-row mb-4" style={{ display: 'flex', gap: '16px' }}>
                <MetricCard
                    title="A receber"
                    metric={`R$ ${Parser.price.brl(summary.future)}`}
                    loading={loading}
                />
                <MetricCard
                    title="Já disponibilizado"
                    metric={`R$ ${Parser.price.brl(summary.paid)}`}
                    loading={loading}
                />
            </div>

            <Text.h4 weight={500} className="">
                Próximas 12 semanas
            </Text.h4>

            <Table
                key={`projection-table-${loading}`}
                data={projection}
                loading={loading}
                loadingRows={4}
                emptyMessage="Nenhuma projeção encontrada"
                layout="banking-funds-projection-layout"
                rowsConfig={rowsConfig}
                enablePagination={false}
            />
        </div>
    );
}
